// srcs/frontend/src/components/proposal/pages/GapPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useProposal } from '../state/ProposalContext.tsx';

import {
  useAiPromptsMutation,
  useLazyGetDataQuery,
  useImportFileMutation,   // POST /files（export）
  useLazyGetFileQuery,     // GET /files（import）
} from '../../../services/solutionAPI.ts';

type ChatMsg = { role: 'User' | 'Assistant'; content: string; ts: number };

type GapExport = {
  data_type: 'gap';
  session_id: string;
  seed_id: string;
  company_info: any;
  needs_info: any;
  seed_info: any;

  analysis_notes: string;
  chat: ChatMsg[];
  report_md: string | null;
  feedback_draft?: string;
  updated_at: number;
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000;
const AUTOSAVE_INTERVAL_MS = 30 * 1000;
const EXPORT_DEBOUNCE_MS = 500;

function isFinishedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'finished' || s === '完了';
}
function isFailedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'failed' || s === '失敗';
}

function fileName(sessionId: string, seedId: string) {
  return `gap_${sessionId}_${seedId}.json`;
}

function byteLength(str: string) {
  try {
    return new Blob([str]).size;
  } catch {
    return str.length;
  }
}

export default function GapPage() {
  const {
    sessionId,
    selectedSeedId,
    companyInfo,
    needsInfo,
    selectedSeedInfo,
    setCompanyInfo,
    setNeedsInfo,
    setSelectedSeedInfo,
    setGapDoneForKey,
  } = useProposal();

  const [postAiPrompt] = useAiPromptsMutation();
  const [triggerGetData] = useLazyGetDataQuery();
  const [postFiles] = useImportFileMutation();
  const [triggerGetFile] = useLazyGetFileQuery();

  // ---- ready 判定を「sessionId & selectedSeedId のみ」に緩和 ----
  const readyBasics = Boolean(sessionId) && Boolean(selectedSeedId);
  const ready = readyBasics;

  // 左側：基本情報 + 操作 + 履歴
  const [analysisNotes, setAnalysisNotes] = useState('');
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [feedback, setFeedback] = useState('');

  // 右側：結果
  const [reportMd, setReportMd] = useState<string | null>(null);

  // 状態
  const [running, setRunning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState<number | null>(null);

  const cancelRef = useRef({ cancelled: false });

  // stale closure回避用（poll内で最新state参照）
  const chatRef = useRef<ChatMsg[]>([]);
  const notesRef = useRef<string>('');
  const reportRef = useRef<string | null>(null);
  const feedbackRef = useRef<string>('');

  useEffect(() => { chatRef.current = chat; }, [chat]);
  useEffect(() => { notesRef.current = analysisNotes; }, [analysisNotes]);
  useEffect(() => { reportRef.current = reportMd; }, [reportMd]);
  useEffect(() => { feedbackRef.current = feedback; }, [feedback]);

  // read-only 表示用
  const companyText = useMemo(() => JSON.stringify(companyInfo ?? {}, null, 2), [companyInfo]);
  const needsText = useMemo(() => JSON.stringify(needsInfo ?? {}, null, 2), [needsInfo]);
  const seedText = useMemo(() => JSON.stringify(selectedSeedInfo ?? {}, null, 2), [selectedSeedInfo]);

  // ----（任意）自動ハイドレーション：company/needs/seed が欠けていたら Seeds の /data から補完 ----
  useEffect(() => {
    const needHydrate =
      readyBasics && (
        !companyInfo ||
        !needsInfo ||
        !selectedSeedInfo
      );

    if (!needHydrate || !sessionId) return;

    (async () => {
      try {
        // Seeds データを取得（seed_id は 'NULL' 固定）
        const res: any = await triggerGetData({
          session_id: sessionId,
          data_type: 'seeds',
          seed_id: 'NULL',
        }).unwrap();

        // 会社/ニーズ情報（存在すれば補完）
        if (!companyInfo && res?.company_info) {
          setCompanyInfo(res.company_info);
        }
        if (!needsInfo && res?.needs_info) {
          setNeedsInfo(res.needs_info);
        }

        // シーズ詳細（選択中の seed_id と一致するものが items にあれば補完）
        const items = Array.isArray(res?.items) ? res.items : [];
        const found = items.find((it: any) => {
          const s = it?.seed_info ?? it?.seed ?? it;
          return s && String(s.seed_id ?? s.id ?? '') === String(selectedSeedId);
        });
        if (!selectedSeedInfo && found) {
          const s = found.seed_info ?? found.seed ?? found;
          const detail = s?.detailed_info ?? {};
          setSelectedSeedInfo({
            seed_id: String(s?.seed_id ?? s?.id ?? ''),
            seed_title: String(s?.title ?? s?.seed_title ?? ''),
            provider: String(s?.provider ?? ''),
            tech_domain: String(detail?.technical_field ?? s?.technical_field ?? ''),
            solvable_issues: Array.isArray(detail?.solvable_issues) ? detail.solvable_issues : [],
            case_studies: Array.isArray(detail?.case_studies) ? detail.case_studies : [],
            function_overview: String(detail?.function_overview ?? ''),
            differentiation_points: String(detail?.differentiation_points ?? ''),
            implementation_requirements: String(detail?.implementation_requirements ?? ''),
            cost_structure: String(detail?.cost_structure ?? ''),
            maturity_level: String(detail?.maturity_level ?? ''),
          });
        }
      } catch {
        // 補完は任意なので失敗しても無視
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyBasics, sessionId, selectedSeedId]);

  // ===== payload builder =====
  const buildPayload = (override?: Partial<GapExport>): GapExport | null => {
    if (!sessionId || !selectedSeedId) return null;
    return {
      data_type: 'gap',
      session_id: sessionId,
      seed_id: selectedSeedId,
      company_info: companyInfo ?? {},   // ← 無くても {} にフォールバック
      needs_info: needsInfo ?? {},       // ← 同上
      seed_info: selectedSeedInfo ?? {}, // ← 同上
      analysis_notes: notesRef.current,
      chat: chatRef.current,
      report_md: reportRef.current,
      feedback_draft: feedbackRef.current,
      updated_at: Date.now(),
      ...(override ?? {}),
    };
  };

  // ★ ai/prompts 用 structured payload builder
  function buildAiPromptPayload(prompt: string, feedbackOverride?: string) {
    return {
      prompt, // ← 既存の prompt をそのまま渡す
      session_id: sessionId!,
      data_type: 'gap' as const,
      seed_id: selectedSeedId!,

      company_info: JSON.stringify(companyInfo ?? {}),
      needs_info: JSON.stringify(needsInfo ?? {}),
      seeds_info: JSON.stringify(selectedSeedInfo ?? {}),

      analysis_conditions: notesRef.current ?? '',
      ...(feedbackOverride
        ? { feedback: feedbackOverride }
        : {}),
    };
  }

  // ===== export（POST /files）=====
  const doExport = async (reason: string, payloadOverride?: GapExport) => {
    if (!sessionId || !selectedSeedId) return;
    const payload = payloadOverride ?? buildPayload();
    if (!payload) return;

    const fname = fileName(sessionId, selectedSeedId);
    const json = JSON.stringify(payload);

    console.groupCollapsed(`[GapPage] POST /files export (${reason})`);
    console.log('endpoint:', 'POST /files');
    console.log('request.file_name:', fname);
    console.log('request.file_context.bytes:', byteLength(json));
    console.log('request.file_context(json):', payload);
    console.groupEnd();

    try {
      const res = await postFiles({
        file_name: fname,
        file_context: json,
      }).unwrap();

      console.groupCollapsed(`[GapPage] POST /files export response (${reason})`);
      console.log('response:', res);
      console.groupEnd();
    } catch (e) {
      console.groupCollapsed(`[GapPage] POST /files export ERROR (${reason})`);
      console.log('error:', e);
      console.groupEnd();
    }
  };

  // ===== /files import（左ペイン & レポート & 履歴の完全復元） =====
  async function importFromFiles(reason: string) {
    if (!sessionId || !selectedSeedId) return;

    const fname = fileName(sessionId, selectedSeedId);

    console.groupCollapsed('[GapPage] GET /files import', reason);
    console.log('file_name:', fname);
    console.groupEnd();

    try {
      const res: any = await triggerGetFile({ file_name: fname }).unwrap();
      const raw = res?.file_context ?? res?.items ?? res?.content ?? res;
      const payload: GapExport = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (!payload || payload.data_type !== 'gap') return;

      // 左ペイン：会社/ニーズ/シーズ
      setCompanyInfo(payload.company_info ?? {});
      setNeedsInfo(payload.needs_info ?? {});
      setSelectedSeedInfo(payload.seed_info ?? {});

      // 左ペイン：特記事項（analysisNotes）・改善フィードバック（feedback）・履歴（chat）
      setAnalysisNotes(payload.analysis_notes ?? '');
      setFeedback(payload.feedback_draft ?? '');
      setChat(Array.isArray(payload.chat) ? payload.chat : []);

      // 右ペイン：レポート
      setReportMd(typeof payload.report_md === 'string' ? payload.report_md : null);

      console.groupCollapsed('[GapPage] import finished (hydrate left pane & report)');
      console.log('chat restored:', Array.isArray(payload.chat) ? payload.chat.length : 0);
      console.groupEnd();
    } catch (e) {
      console.warn('[GapPage] import failed (no saved file yet?)', e);
    }
  }

  // ===== chat/report更新のたびに export（デバウンス・自動保存）=====
  const exportTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;

    if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    exportTimerRef.current = window.setTimeout(() => {
      doExport('state_change(chat/report)');
      exportTimerRef.current = null;
    }, EXPORT_DEBOUNCE_MS);

    return () => {
      if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    };
  }, [
    readyBasics,
    sessionId,
    selectedSeedId,
    chat,
    reportMd,      // ← 右側成果物のみ
  ]);

  // ===== autosave（保険の自動保存）=====
  useEffect(() => {
    if (!readyBasics) return;
    const id = window.setInterval(() => {
      doExport('autosave');
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyBasics, sessionId, selectedSeedId]);

  // ===== prompt 作成 =====
  function buildConversationBlock(maxTurns = 10) {
    const tail = chatRef.current.slice(-maxTurns);
    if (tail.length === 0) return '（会話履歴なし）';
    return tail.map((m) => `${m.role}: ${m.content}`).join('\n');
  }

  function buildPromptInitial(): string {
    return `
あなたは経験豊富なソリューションアナリストです。提供されたニーズ情報とシーズ情報を詳細に分析し、まずギャップの存在を判定し、ギャップがある場合はニーズを満たすために不足している要素を具体的かつ根拠とともに特定してください。

## 入力情報
【企業情報（参考）】
${JSON.stringify(companyInfo ?? {}, null, 2)}

【ニーズ情報】
${JSON.stringify(needsInfo ?? {}, null, 2)}

【シーズ情報（選択済み）】
${JSON.stringify(selectedSeedInfo ?? {}, null, 2)}

【分析条件・特記事項（任意）】
${notesRef.current || '（特になし）'}

## 重要な前提条件
### シーズの取り扱いについて
- **単一シーズの場合**: そのシーズ単体でのニーズ対応を分析
- **複数シーズの場合**: 各シーズを組み合わせた統合ソリューションとしての対応を分析
  - 各シーズの相互補完関係を評価
  - シーズ間の連携・統合による相乗効果を考慮
  - 複数シーズ組み合わせ時の新たな制約や課題も分析
  - 統合コスト、統合期間、統合リスクを含めて評価

## 分析手順
### 1. ニーズ要件の構造化
提供されたニーズ情報から、少なくとも以下を抽出・整理してください：
- 主要要件（機能/非機能）
- 目標値/評価指標（KPI・SLA 等）
- 制約条件（期間・予算・体制・セキュリティ等）
- 依存関係/前提（既存システム・運用）

### 2. シーズ機能の詳細分析
提供されたシーズ情報（単一/複数）から、少なくとも以下を整理してください：
- 提供機能/性能と限界
- 導入要件（技術/プロセス/人的）
- コスト/期間/成熟度
- 既知のリスク・制約

複数シーズの場合は加えて：
- **個別分析**（上記を各シーズごとに実施）
- **関係性分析**（相互補完・重複・相乗効果・統合時の課題）
- **統合像**（総合機能/性能、統合コスト、導入順序、統合リスク）

### 3. ギャップ存在判定（要件対応表）
各ニーズ要件に対してシーズ（統合ソリューション）の対応状況を評価してください。表を用いて明示します：

| ニーズ要件 | 目標値 | シーズ対応状況 | 対応度 | 判定理由 | 対応シーズ |
|---|---|---|---|---|---|
| [要件1] | [目標1] | [対応状況1] | [○/△/×] | [理由1] | [シーズA/シーズB+C/統合効果] |

**対応度判定基準**
- ○：要件を完全に満たす
- △：部分的に満たすが追加対応が必要
- ×：満たすことが困難

複数シーズの場合は **統合効果評価/統合課題評価/最適組み合わせ提案** も記載。

**総合判定**
- 重要なギャップなし（○中心、軽微な△のみ）
- 重要なギャップあり（重要な△または×が存在）

### 4. 詳細ギャップ分析（重要なギャップありの場合）
以下のテンプレを繰り返し記載（\`\`\` の中身のみ整形して埋めてください）：
\`\`\`
#### ギャップ項目：[具体的なギャップ名]

**ギャップの詳細内容：**
- 現状要求：
- シーズ提供範囲：
- 不足要素：

**根拠・理由：**
- ニーズ分析根拠：
- シーズ限界根拠：
- 統合時の考慮事項（複数時）：
- 過去事例からの示唆：

**補完に必要な具体的要素：**
- 技術要素：
- 人的要素：
- プロセス要素：
- 統合要素（複数時）：
- その他要素：

**補完方法の選択肢：**
1. [選択肢1] - メリット/デメリット - 統合への影響
2. [選択肢2] - メリット/デメリット - 統合への影響
3. [選択肢3] - メリット/デメリット - 統合への影響

**コスト・期間・リスク評価：**
- 概算コスト（根拠）：
- 必要期間（根拠）：
- 主要リスク（確率/影響）：
- 実現可能性（高/中/低、根拠）：
\`\`\`

### 5. ギャップ補完の優先順位付け
- 影響度（ビジネス/KPI）
- 実現容易性（コスト・期間・体制）
- リスク（技術/運用/統合）
- リターン（費用対効果）
- （複数シーズ時）**統合への影響度**

### 6. 複数シーズの場合の追加分析
- **統合戦略**（推奨組み合わせ、段階導入、統合アーキテクチャ）
- **統合効果**（相乗効果、コスト効率、リスク軽減）
- **統合課題と対策**

## 出力形式
### 重要なギャップなし
\`\`\`md
## ギャップ分析結果

### ギャップ存在判定
**総合判定：** 重要なギャップなし

### シーズ構成
[単一/複数統合ソリューションの概要]

### 要件対応状況
[上記の表]

### 簡易所見
- 対応可能性：
- 軽微な調整事項：
- 推奨アプローチ：
- （複数時）統合に関する所見：
\`\`\`

### 重要なギャップあり
\`\`\`md
## 詳細ギャップ分析結果

### ギャップ存在判定
**総合判定：** 重要なギャップあり

### シーズ構成
[単一/複数統合ソリューションの概要]

### 要件対応状況
[上記の表]

### 詳細ギャップ分析
[テンプレを各ギャップで反復]

### ギャップ補完の優先順位
1. [最優先ギャップ]（理由/推奨補完方法）
2. [次優先ギャップ]（理由/推奨補完方法）

### 複数シーズ統合分析（該当時）
[統合戦略/効果/課題と対策]

### 全体所見
- 補完可能性：
- 主要制約：
- 推奨アプローチ：
- （複数時）統合ソリューションとしての評価：
\`\`\`

**注意**：推定や仮定を行う場合は、根拠（情報源/類似事例/一般相場等）を必ず明記してください。
`.trim();
  }

  function buildPromptImprove(userFeedback: string): string {
    return `
あなたはGAP分析の改善担当です。前回のレポート・会話履歴・フィードバックを反映して改善してください。

【前回レポート（Markdown）】
${reportRef.current ?? '（まだレポートなし）'}

【会話履歴（直近）】
${buildConversationBlock(10)}

【分析条件・特記事項（任意）】
${notesRef.current || '（特になし）'}

【ユーザーのフィードバック】
${userFeedback}

【出力要件】
- 改善したGAP分析（Markdown、初回と同じ章立て）
- 変更点まとめ（どこを、どう変えたか）
- 改善理由（根拠・整合性）
`.trim();
  }

  // ===== /data から Markdown を取り出す =====
  function extractReportMarkdown(data: any): string {
    if (typeof data?.items === 'string') return data.items;
    if (typeof data?.report_md === 'string') return data.report_md;
    if (typeof data?.items?.report_md === 'string') return data.items.report_md;
    if (typeof data?.items?.md === 'string') return data.items.md;

    for (const k of Object.keys(data ?? {})) {
      if (k.startsWith('items') && typeof (data as any)[k] === 'string') {
        return String((data as any)[k]);
      }
    }
    return JSON.stringify(data?.items ?? data, null, 2);
  }

  // ===== GET /data ポーリング =====
  async function pollUntilFinished(reason: string, baseChatSnapshot: ChatMsg[]) {
    if (!sessionId || !selectedSeedId) return;

    setRunning(true);
    setTimedOut(false);
    cancelRef.current.cancelled = false;

    const start = Date.now();
    let attempt = 0;

    try {
      while (Date.now() - start < POLL_MAX_MS) {
        if (cancelRef.current.cancelled) break;

        attempt += 1;
        const query = {
          session_id: sessionId,
          data_type: 'gap' as const,
          seed_id: selectedSeedId,
        };

        console.groupCollapsed(`[GapPage] GET /data poll #${attempt} (${reason})`);
        console.log('endpoint:', 'GET /data');
        console.log('query:', query);
        console.groupEnd();

        let data: any;
        try {
          data = await triggerGetData(query).unwrap();

          console.groupCollapsed(`[GapPage] GET /data response #${attempt} (${reason})`);
          console.log('status:', data?.status);
          console.log('updated_date:', data?.updated_date);
          console.log('response(raw):', data);
          console.groupEnd();
        } catch (e) {
          console.groupCollapsed(`[GapPage] GET /data ERROR #${attempt} (${reason})`);
          console.log('error:', e);
          console.groupEnd();

          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          continue;
        }

        setLastFetchAt(Date.now());

        if (isFinishedStatus(data?.status)) {
          const md = extractReportMarkdown(data);

          console.groupCollapsed(`[GapPage] finished (${reason})`);
          console.log('final markdown length:', md.length);
          console.log('final markdown:', md);
          console.groupEnd();

          const assistantMsg: ChatMsg = { role: 'Assistant', content: md, ts: Date.now() };
          const chatNext = [...baseChatSnapshot, assistantMsg];

          setReportMd(md);
          setChat(chatNext);

          setGapDoneForKey(sessionId, selectedSeedId, true);

          // 自動保存（/files）
          await doExport(`response(${reason})`, buildPayload({
            chat: chatNext,
            report_md: md,
            updated_at: Date.now(),
          }) as GapExport);

          return;
        }

        if (isFailedStatus(data?.status)) {
          const msg = `GAP分析 failed: ${String(data?.status)}`;
          const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
          const chatNext = [...baseChatSnapshot, assistantMsg];

          console.groupCollapsed(`[GapPage] failed (${reason})`);
          console.log('message:', msg);
          console.log('response:', data);
          console.groupEnd();

          setReportMd(msg);
          setChat(chatNext);

          await doExport(`response_failed(${reason})`, buildPayload({
            chat: chatNext,
            report_md: msg,
            updated_at: Date.now(),
          }) as GapExport);

          return;
        }

        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }

      setTimedOut(true);
    } finally {
      setRunning(false);
    }
  }

  // ===== 初回実行：POST /ai/prompts → poll =====
  const onRunGap = async () => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;

    const prompt = buildPromptInitial();
    const userMsg: ChatMsg = { role: 'User', content: 'GAP分析を実行', ts: Date.now() };

    const baseChat = chatRef.current;
    const chatAfterUser = [...baseChat, userMsg];

    setChat(chatAfterUser);
    setReportMd(null);
    setTimedOut(false);

    // ユーザー発話時点でも保存
    await doExport('user_message_run', buildPayload({
      chat: chatAfterUser,
      report_md: null,
      updated_at: Date.now(),
    }) as GapExport);

    const body = buildAiPromptPayload(prompt);

    console.groupCollapsed('[GapPage] POST /ai/prompts (initial)');
    console.log('endpoint:', 'POST /ai/prompts');
    console.log('body.session_id:', body.session_id);
    console.log('body.data_type:', body.data_type);
    console.log('body.seed_id:', body.seed_id);
    console.log('body.prompt.length:', body.prompt.length);
    console.log('body.prompt:', body.prompt);
    console.groupEnd();

    try {
      await postAiPrompt(body).unwrap();
    } catch (e) {
      console.groupCollapsed('[GapPage] POST /ai/prompts ERROR (initial)');
      console.log('error:', e);
      console.groupEnd();

      const msg = `POST /ai/prompts に失敗しました: ${String((e as any)?.message ?? e)}`;
      const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
      const chatNext = [...chatAfterUser, assistantMsg];

      setChat(chatNext);
      setReportMd(msg);

      await doExport('ai_prompts_failed', buildPayload({
        chat: chatNext,
        report_md: msg,
        updated_at: Date.now(),
      }) as GapExport);

      return;
    }

    // 結果取得：/data ポーリング
    await pollUntilFinished('initial', chatAfterUser);
  };

  // ===== 改善：POST /ai/prompts → poll =====
  const onImprove = async () => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;
    const fb = feedback.trim();
    if (!fb) return;

    const prompt = buildPromptImprove(fb);
    const userMsg: ChatMsg = { role: 'User', content: fb, ts: Date.now() };
    setFeedback('');
    setTimedOut(false);

    const baseChat = chatRef.current;
    const chatAfterUser = [...baseChat, userMsg];
    setChat(chatAfterUser);

    await doExport('user_message_improve', buildPayload({
      chat: chatAfterUser,
      updated_at: Date.now(),
    }) as GapExport);

    const body = buildAiPromptPayload(prompt, fb);

    console.groupCollapsed('[GapPage] POST /ai/prompts (improve)');
    console.log('endpoint:', 'POST /ai/prompts');
    console.log('body.session_id:', body.session_id);
    console.log('body.data_type:', body.data_type);
    console.log('body.seed_id:', body.seed_id);
    console.log('body.prompt.length:', body.prompt.length);
    console.log('body.prompt:', body.prompt);
    console.groupEnd();

    try {
      await postAiPrompt(body).unwrap();
    } catch (e) {
      console.groupCollapsed('[GapPage] POST /ai/prompts ERROR (improve)');
      console.log('error:', e);
      console.groupEnd();

      const msg = `POST /ai/prompts（改善）に失敗しました: ${String((e as any)?.message ?? e)}`;
      const assistantMsg: ChatMsg = { role: 'Assistant', content: msg, ts: Date.now() };
      const chatNext = [...chatAfterUser, assistantMsg];

      setChat(chatNext);
      setReportMd(msg);

      await doExport('ai_prompts_failed_improve', buildPayload({
        chat: chatNext,
        report_md: msg,
        updated_at: Date.now(),
      }) as GapExport);

      return;
    }

    await pollUntilFinished('improve', chatAfterUser);
  };

  // ===== 再取得（/files → /data）===== ★変更
  const onRefetch = async () => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;
    setTimedOut(false);

    console.groupCollapsed('[GapPage] refetch start');
    console.log('action:', 'GET /files (import) -> GET /data (gap)');
    console.groupEnd();

    // 1) まず /files から復元（左ペインとレポート・履歴を戻す）
    await importFromFiles('refetch');

    // 2) その後 /data をポーリングして最新化（完了時に自動保存も走る）
    await pollUntilFinished('refetch', chatRef.current);
  };

  // ===== 手動保存（/files）=====
  const onManualSave = async () => {
    if (!sessionId || !selectedSeedId) return;
    await doExport('manual_save');
  };

  useEffect(() => () => { cancelRef.current.cancelled = true; }, []);

  return (
    <Box sx={{ height: 'calc(100vh - 32px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 2 }}>
      {/* 左 */}
      <Box sx={{ overflow: 'auto', pr: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>GAP分析</Typography>

        {!readyBasics && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Seedsでシーズ選択し、session_id / seed_id を確定してください。
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="contained" disabled={!readyBasics || running} onClick={onRunGap}>
              ギャップ分析実行
            </Button>

            {running && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">取得中…（最大5分）</Typography>
              </Stack>
            )}
            {lastFetchAt && (
              <Typography variant="caption" color="text.secondary">
                最終取得: {new Date(lastFetchAt).toLocaleTimeString()}
              </Typography>
            )}
          </Stack>

          {timedOut && (
            <Alert severity="info" sx={{ mt: 1 }}>
              5分経っても finished になりませんでした。再取得してください。
            </Alert>
          )}
        </Paper>

        {/* 参照セクション（内容が空でも read-only 表示でOK） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>企業情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={companyText} multiline minRows={5} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>ニーズ情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={needsText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>選択シーズ情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={seedText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>特記事項（任意）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              placeholder="例）段階導入 / 既存連携必須 / ROIは2年回収重視 など"
              value={analysisNotes}
              onChange={(e) => setAnalysisNotes(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              size="small"
              disabled={!readyBasics || running}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>改善（フィードバック）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="追加情報や見積条件の変更など"
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              disabled={!readyBasics || running}
            />
            <Button variant="contained" disabled={!readyBasics || running || !feedback.trim()} onClick={onImprove}>
              改善して再分析
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>分析履歴（改善の過程）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {chat.length === 0 ? (
              <Typography variant="body2" color="text.secondary">まだ履歴はありません。</Typography>
            ) : (
              <Stack spacing={1}>
                {chat.map((m, idx) => (
                  <Box key={idx} sx={{ p: 1, borderRadius: 1, bgcolor: m.role === 'User' ? '#e3f2fd' : '#f5f5f5' }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.role} ・ {new Date(m.ts).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* 右：結果 */}
      <Box sx={{ overflow: 'auto', pl: 1 }}>
        {/* 右上 sticky：再取得 + 保存 */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, pb: 1, bgcolor: 'background.default' }}>
          <Button variant="outlined" size="small" disabled={!readyBasics || running} onClick={onRefetch}>再取得</Button>
          <Button variant="outlined" size="small" disabled={!readyBasics || running} onClick={onManualSave}>保存</Button>
        </Box>
        {!reportMd ? (
          <Box />
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              GAP分析レポート
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ bgcolor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {reportMd}
              </ReactMarkdown>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}